import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Plus, Upload, Download, Package, Store, Warehouse, AlertTriangle, XCircle,
  Loader2, Filter, ChevronDown, ChevronUp, ShoppingBag, ArrowRightLeft, Trash2,
} from 'lucide-react';
import StockArticleListCard from './StockArticleListCard';
import StockDetailPanel from './StockDetailPanel';
import { fmtEuro } from './stockUtils';

const StockAtelierView = ({
  stats,
  loading,
  articles,
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
  magasinFilter,
  onMagasinFilterChange,
  magasinsList,
  filtersOpen,
  onToggleFiltersOpen,
  selectedId,
  onSelectId,
  promosByArticle,
  isAdmin,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onImport,
  onExport,
  onInvoiceImport,
  onTransfer,
  onPromo,
  onLabel,
  onDelete,
  onToggleOnline,
  onBulkOnline,
  onBulkOffline,
  onBulkTransfer,
  onBulkDelete,
  t,
}) => {
  const selected = articles.find((a) => a.id === selectedId) || null;
  const allBulkSelected = articles.length > 0 && articles.every((a) => selectedIds.includes(a.id));
  const someBulkSelected = articles.some((a) => selectedIds.includes(a.id));

  return (
    <div className="fade-in">
      <div className="mb-3 px-1 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-white uppercase tracking-wide">{t('stock.manage')}</h2>
          <p className="text-xs text-gray-500 mt-0.5">Inventaire boutique et dépôt</p>
        </div>
        <Link
          to="/stock/ajouter"
          className="min-h-[44px] px-4 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold text-sm flex items-center gap-2"
          data-testid="add-stock-btn"
        >
          <Plus size={18} /> {t('stock.addArticle')}
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3" data-testid="stock-kpi">
          {[
            { icon: Package, value: stats.total_produits, label: 'Produits', color: 'text-white' },
            { icon: Store, value: stats.total_boutique || 0, label: 'Boutique', color: 'text-emerald-400' },
            { icon: Warehouse, value: stats.total_depot || 0, label: 'Dépôt', color: 'text-gray-200' },
            { icon: null, value: `${fmtEuro(stats.valeur_stock)} €`, label: 'Valeur', color: 'text-white', small: true },
            { icon: AlertTriangle, value: stats.stock_faible, label: 'Faible', color: 'text-red-400' },
            { icon: XCircle, value: stats.ruptures, label: 'Ruptures', color: 'text-red-400' },
          ].map((kpi, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#151820] p-3">
              {kpi.icon && <kpi.icon className={`w-5 h-5 text-gray-500 mb-1 ${kpi.color}`} />}
              <div className={`${kpi.small ? 'text-base' : 'text-2xl'} font-black tabular-nums ${kpi.color}`}>{kpi.value}</div>
              <div className="text-[10px] text-gray-500 uppercase">{kpi.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-[#151820] p-3 sm:p-4 mb-3 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('stock.search')}
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm min-h-[44px]"
            data-testid="search-stock"
          />
        </div>

        <button
          type="button"
          onClick={onToggleFiltersOpen}
          className="xl:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] rounded-lg border border-white/10 bg-black/20 text-sm font-semibold text-gray-200"
          data-testid="stock-filters-toggle"
        >
          <span className="flex items-center gap-2"><Filter size={16} /> Filtres & actions</span>
          {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        <div className={`${filtersOpen ? 'block' : 'hidden'} xl:block space-y-3`}>
          <div className="flex flex-wrap gap-2 items-center">
            <select value={filter} onChange={(e) => onFilterChange(e.target.value)} className="rounded-lg bg-black/30 border border-white/10 text-white text-sm px-3 py-2.5 min-h-[44px]" data-testid="stock-filter-select">
              <option value="all">{t('common.all')}</option>
              <option value="faible">{t('stock.lowStockAlert')}</option>
              <option value="rupture">{t('dash.outOfStock')}</option>
            </select>
            <span className="text-xs text-gray-500">Magasin :</span>
            <button type="button" onClick={() => onMagasinFilterChange('')} className={`px-3 py-2 min-h-[40px] rounded-lg text-xs font-bold border ${!magasinFilter ? 'border-red-500/50 bg-red-950/30 text-white' : 'border-white/10 text-gray-400'}`}>Tous</button>
            {magasinsList.map((m) => (
              <button key={m.code} type="button" onClick={() => onMagasinFilterChange(m.code)} className={`px-3 py-2 min-h-[40px] rounded-lg text-xs font-bold border ${magasinFilter === m.code ? 'border-red-500/50 bg-red-950/30 text-white' : 'border-white/10 text-gray-400'}`}>{m.code}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onImport} className="min-h-[40px] px-3 rounded-lg border border-white/10 bg-white/5 text-xs font-semibold text-gray-300 flex items-center gap-1.5" data-testid="stock-import-csv-btn"><Upload size={14} /> Import CSV</button>
            <button type="button" onClick={onInvoiceImport} className="min-h-[40px] px-3 rounded-lg border border-white/10 bg-white/5 text-xs font-semibold text-gray-300 flex items-center gap-1.5" data-testid="stock-import-invoice-btn">Import Facture IA</button>
            <button type="button" onClick={onExport} className="min-h-[40px] px-3 rounded-lg border border-white/10 bg-white/5 text-xs font-semibold text-gray-300 flex items-center gap-1.5" data-testid="stock-export-csv"><Download size={14} /> Export CSV</button>
          </div>
          {isAdmin && selectedIds.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10" data-testid="stock-bulk-actions">
              <button type="button" onClick={() => onBulkOnline(true)} className="min-h-[40px] px-3 rounded-lg border border-emerald-500/30 text-emerald-300 text-xs font-bold flex items-center gap-1"><ShoppingBag size={14} /> En ligne ({selectedIds.length})</button>
              <button type="button" onClick={() => onBulkOffline(false)} className="min-h-[40px] px-3 rounded-lg border border-white/10 text-gray-300 text-xs font-bold">Hors ligne ({selectedIds.length})</button>
              <button type="button" onClick={onBulkTransfer} className="min-h-[40px] px-3 rounded-lg border border-white/10 text-gray-300 text-xs font-bold flex items-center gap-1"><ArrowRightLeft size={14} /> Transfert ({selectedIds.length})</button>
              <button type="button" onClick={onBulkDelete} className="min-h-[40px] px-3 rounded-lg bg-red-950/40 text-red-300 text-xs font-bold flex items-center gap-1"><Trash2 size={14} /> Supprimer ({selectedIds.length})</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-3 min-h-[calc(100vh-320px)]">
        <div className="xl:w-[42%] shrink-0 flex flex-col min-h-0">
          {loading ? (
            <div className="rounded-xl border border-white/10 bg-[#151820] p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-500" /></div>
          ) : articles.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#151820] p-12 text-center text-gray-500 text-sm" data-testid="stock-empty-mobile">{t('stock.noItems')}</div>
          ) : (
            <>
              {isAdmin && (
                <label
                  className="flex items-center gap-2 px-2 py-2 mb-1 rounded-lg border border-white/10 bg-black/20 cursor-pointer min-h-[44px]"
                  data-testid="stock-select-all"
                >
                  <input
                    type="checkbox"
                    checked={allBulkSelected}
                    ref={(el) => { if (el) el.indeterminate = someBulkSelected && !allBulkSelected; }}
                    onChange={onToggleSelectAll}
                    className="w-4 h-4 rounded"
                    aria-label="Tout sélectionner"
                  />
                  <span className="text-xs font-semibold text-gray-300">
                    Tout sélectionner
                    {someBulkSelected ? ` (${selectedIds.filter((id) => articles.some((a) => a.id === id)).length}/${articles.length})` : ` (${articles.length})`}
                  </span>
                </label>
              )}
            <div className="flex flex-col gap-3 overflow-y-auto max-h-[calc(100vh-320px)] pr-1 pb-1" data-testid="stock-mobile-cards">
              {articles.map((article) => (
                <StockArticleListCard
                  key={article.id}
                  article={article}
                  selected={article.id === selectedId}
                  promo={promosByArticle[article.id]}
                  onSelect={() => onSelectId(article.id)}
                  isAdmin={isAdmin}
                  bulkSelected={selectedIds.includes(article.id)}
                  onToggleBulk={onToggleSelect}
                />
              ))}
            </div>
            </>
          )}
          <p className="text-[10px] text-gray-600 mt-2 px-1" data-testid="stock-list-count">{articles.length} article(s)</p>
        </div>

        <div className="hidden xl:block xl:flex-1 rounded-xl border border-white/10 bg-[#151820] p-4 min-h-[400px]" data-testid="stock-table-desktop">
          <StockDetailPanel
            article={selected}
            promo={selected ? promosByArticle[selected.id] : null}
            isAdmin={isAdmin}
            onTransfer={onTransfer}
            onPromo={onPromo}
            onLabel={onLabel}
            onDelete={onDelete}
            onToggleOnline={onToggleOnline}
          />
        </div>
      </div>
    </div>
  );
};

export default StockAtelierView;
